import React, { useState, useRef, useImperativeHandle, forwardRef, useMemo, useEffect } from 'react';
import {
  Box,
  Chip,
  CardActionArea,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PeopleAltOutlined,
  CloudUpload as UploadIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { Customer, Company, PaginatedResponse, SearchParams, Role } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import HelpTooltip from '../common/HelpTooltip';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import IdentityAvatar from '../common/IdentityAvatar';
import { useMobileTopBarAction, useMobileTopBarActions } from '../common/MobileTopBarActionsContext';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import { useIsMobileShell } from '../common/useIsMobileShell';
import PaginationFooter from '../common/PaginationFooter';
import ListSurface from '../common/ListSurface';
import ColumnVisibilityMenu from '../common/ColumnVisibilityMenu';
import NotebookColumnTab from '../common/NotebookColumnTab';
import { apiClient } from '../../services/api';
import { getColumnKey } from '../../utils/table';
import { sortingFromTableSort, tableSortFromSorting } from '../../utils/tableSort';
import { getPersonInitials } from '../../utils/initials';
import { createCustomerColumns } from './customerColumns';
import CustomerActionsMenu from './CustomerActionsMenu';
// Removed useColumnConfiguration import - implementing directly

interface CustomerListProps {
  paginatedData: PaginatedResponse<Customer> | null;
  loading: boolean;
  searchParams: SearchParams;
  onSearch: (name?: string) => void;
  onPaginationModelChange: (model: { page: number, pageSize: number }) => void;
  onSortChange: (field?: string, direction?: 'asc' | 'desc') => void;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onAdd: () => void;
  onBulkUpload: () => void;
  onCustomerClick?: (customerId: string, customerName: string) => void;
  onCompanyFilter?: (companyId: string) => void;
  autoFocusSearch?: boolean;
}

export interface CustomerListRef {
  focusSearch: () => void;
}

const CustomerList = forwardRef<CustomerListRef, CustomerListProps>(({
  paginatedData,
  loading,
  searchParams,
  onSearch,
  onPaginationModelChange,
  onSortChange,
  onEdit,
  onDelete,
  onAdd,
  onBulkUpload,
  onCustomerClick,
  onCompanyFilter,
  autoFocusSearch = false,
}, ref) => {
  const { user } = useAuth();
  const canManageCustomers = user?.role !== Role.EMPLOYEE;
  const { t } = useLanguage();
  const isMobile = useIsMobileShell();
  const isDesktopTable = !isMobile;
  const mobileTopBarActions = useMobileTopBarActions();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTerm = searchParams.name || '';
  const [contextMenuCustomer, setContextMenuCustomer] = useState<Customer | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');

  useEffect(() => {
    setSelectedCompany(searchParams.companyId || '');
  }, [searchParams.companyId]);

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }));

  const { language } = useLanguage();
  
  const getCustomersHelpContent = () => {
    if (language === 'tr') {
      return (
        <Box>
          <Typography variant="body2" paragraph><strong>Müşterileri arayın ve hesap detaylarını inceleyin.</strong></Typography>
          <Typography variant="subtitle2" gutterBottom>🔍 <strong>Arama:</strong></Typography>
          <Typography variant="body2" paragraph>
            • Arama alanına ad veya soyad yazın<br/>
            • Türkçe karakterler desteklenir (ç, ğ, ı, ö, ş, ü)
            {isDesktopTable && <><br/>• Tabloda gösterilen sütunları özelleştirmek için Sütunlar düğmesini kullanın</>}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>👥 <strong>Müşteri Detayları:</strong></Typography>
          <Typography variant="body2" paragraph>
            • Müşteri adına tıklayarak detay ve hesap geçmişini açın<br/>
            • Müşteri detaylarında borç ve ödeme geçmişini inceleyin
          </Typography>
          {canManageCustomers && <>
            <Typography variant="subtitle2" gutterBottom>➕ <strong>Müşteri Ekleme ve Yönetimi:</strong></Typography>
            {user?.role === Role.SUPER_ADMIN ? <Typography variant="body2" paragraph>
              • CSV ile aktarımda hedef şirketi seçin<br/>
              • Mevcut Müşteri Ekle formunda şirket seçimi yoktur; Süper Yönetici olarak tek müşteri oluşturamazsınız<br/>
              • İşlemler (⋮) menüsünden mevcut müşterileri düzenleyin veya silin
            </Typography> : <Typography variant="body2" paragraph>
              • Müşteri Ekle ile yeni kayıt oluşturun; ad ve soyad zorunludur, telefon numarası isteğe bağlıdır<br/>
              • Toplu Yükleme ile CSV şablonunu indirip birden fazla müşteri aktarın<br/>
              • İşlemler (⋮) menüsünden müşteriyi düzenleyin veya silin
            </Typography>}
          </>}
          <Typography variant="subtitle2" gutterBottom>💡 <strong>İpuçları:</strong></Typography>
          <Typography variant="body2">
            • Telefon numarası zorunlu değildir<br/>
            • Satış veya borç kaydı bulunan müşteri silinemez
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box>
          <Typography variant="body2" paragraph><strong>Find customers and review their account details.</strong></Typography>
          <Typography variant="subtitle2" gutterBottom>🔍 <strong>Search:</strong></Typography>
          <Typography variant="body2" paragraph>
            • Search by first or last name<br/>
            • Turkish characters are supported (ç, ğ, ı, ö, ş, ü)
            {isDesktopTable && <><br/>• Use the Columns button to customize which columns appear in the table</>}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>👥 <strong>Customer Details:</strong></Typography>
          <Typography variant="body2" paragraph>
            • Select a customer’s name to open their details and account history<br/>
            • Review debt and payment history in customer details
          </Typography>
          {canManageCustomers && <>
            <Typography variant="subtitle2" gutterBottom>➕ <strong>Adding and Managing Customers:</strong></Typography>
            {user?.role === Role.SUPER_ADMIN ? <Typography variant="body2" paragraph>
              • Select the target company when importing by CSV<br/>
              • The current Add Customer form has no company selector, so Super Administrators cannot create a single customer with it<br/>
              • Use the actions (⋮) menu to edit or delete existing customers
            </Typography> : <Typography variant="body2" paragraph>
              • Choose Add Customer; first and last name are required, phone number is optional<br/>
              • Use Bulk Upload to download the CSV template and import multiple customers<br/>
              • Use the actions (⋮) menu to edit or delete a customer
            </Typography>}
          </>}
          <Typography variant="subtitle2" gutterBottom>💡 <strong>Tips:</strong></Typography>
          <Typography variant="body2">
            • Phone number is optional<br/>
            • A customer with sales or debt records cannot be deleted
          </Typography>
        </Box>
      );
    }
  };

  const canWrite = user?.role === Role.COMPANY_ADMIN;
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  useMobileTopBarAction(isMobile && canWrite && mobileTopBarActions ? {
    id: 'customers-import-csv',
    label: t('customers.importCsv'),
    icon: <UploadIcon fontSize="small" />,
    order: 10,
    onSelect: () => onBulkUpload(),
  } : null);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    firstName: true,
    phone: true,
    email: true,
    taxIdentificationNumber: false,
    address: true,
    companyName: true,
    actions: true,
  });
  
  // Column menu state
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Column visibility handlers
  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const toggleColumnVisibility = (columnKey: keyof typeof columnVisibility) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const loadCompanies = React.useCallback(async () => {
    if (isSuperAdmin) {
      try {
        const companiesData = await apiClient.getCompanies();
        setCompanies(companiesData);
      } catch {
        // Company filters are optional; the main customer query owns visible errors.
      }
    }
  }, [isSuperAdmin]);

  React.useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleCompanyChange = React.useCallback((companyId: string) => {
    setSelectedCompany(companyId);
    onCompanyFilter?.(companyId);
  }, [onCompanyFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(event.target.value || undefined);
  };

  const handleClearSearch = () => {
    onSearch(undefined);
  };

  const handleRowRightClick = (event: React.MouseEvent, customer: Customer) => {
    if (!canWrite) return;
    event.preventDefault();
    setContextMenuCustomer(customer);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenuPosition(null);
    setContextMenuCustomer(null);
  };

  const allColumns = useMemo(() => createCustomerColumns({
    t, language, canWrite, isSuperAdmin, onEdit, onDelete, onCustomerClick,
  }), [t, language, canWrite, isSuperAdmin, onEdit, onDelete, onCustomerClick]);

  // Filter columns based on visibility
  const columns = useMemo(() => {
    return allColumns.filter((column) => {
      const key = getColumnKey(column);
      if (key === 'companyName' && !isSuperAdmin) return false;
      if (key === 'actions' && !canWrite) return false;
      return columnVisibility[key as keyof typeof columnVisibility] !== false;
    });
  }, [allColumns, columnVisibility, isSuperAdmin, canWrite]);

  // TanStack React Table configuration
  const [sorting, setSorting] = useState<SortingState>(() => sortingFromTableSort(searchParams));

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: searchParams.page ?? 0,
    pageSize: searchParams.size ?? 10,
  });

  const table = useReactTable({
    data: paginatedData?.content || [],
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      const nextSort = tableSortFromSorting(newSorting);
      onSortChange(nextSort.sort, nextSort.direction);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
      onPaginationModelChange({
        page: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    sortDescFirst: false,
    manualPagination: true,
    manualSorting: true,
    pageCount: paginatedData?.totalPages || 0,
    rowCount: paginatedData?.totalElements || 0,
  });

  // Sync external state changes to table state
  React.useEffect(() => {
    setPagination({
      pageIndex: searchParams.page ?? 0,
      pageSize: searchParams.size ?? 10,
    });
  }, [searchParams.page, searchParams.size]);

  React.useEffect(() => {
    setSorting(sortingFromTableSort({ sort: searchParams.sort, direction: searchParams.direction }));
  }, [searchParams.sort, searchParams.direction]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pb: isMobile && paginatedData ? 'calc(61px + env(safe-area-inset-bottom))' : 0 }}>
      {!isMobile && <PageHeader
        title={t('customers.title')}
        icon={<PeopleAltOutlined />}
        accent="customers"
        mobileMode="hidden"
        titleAccessory={<HelpTooltip helpId="customers-page-help" title={t('help.customers.title')} content={getCustomersHelpContent()} maxWidth={700} placement="bottom-start" />}
        description={t('customers.directoryDescription')}
        actions={canWrite ? <Box sx={{ display: 'flex', gap: 1 }}>
          <PageHeaderActionButton startIcon={<UploadIcon />} onClick={onBulkUpload}>{t('customers.bulkUpload')}</PageHeaderActionButton>
          <PageHeaderActionButton data-testid="customer-add" startIcon={<AddIcon />} onClick={onAdd}>{t('customers.addCustomer')}</PageHeaderActionButton>
        </Box> : undefined}
      />}
      {isMobile && canWrite && !mobileTopBarActions && <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}><Button variant="text" size="small" startIcon={<UploadIcon />} onClick={onBulkUpload}>{t('customers.bulkUpload')}</Button></Box>}
      {isMobile && <HelpTooltip helpId="customers-page-help" title={t('help.customers.title')} content={getCustomersHelpContent()} maxWidth={700} placement="bottom-start" />}

      <ListSurface sx={{ overflow: { xs: 'hidden', md: 'visible' } }}>
        <ListSurface.Toolbar sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 1.5, md: 2.5 }, pb: { xs: 0, md: 2.5 } }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center', flexDirection: 'row' }}>
            <TextField
              inputProps={{ 'data-testid': 'customer-search', 'aria-label': t('customers.searchPlaceholder') }}
              ref={searchInputRef}
              autoFocus={autoFocusSearch}
              placeholder={t('customers.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              size={isMobile ? 'small' : 'medium'}
              fullWidth
              sx={{
                flex: 1,
                minWidth: 0,
                ...(isMobile ? {
                  '& .MuiOutlinedInput-root': {
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    '& fieldset, &:hover fieldset': { border: 0 },
                    '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' },
                  },
                } : { '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }),
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: searchTerm ? <InputAdornment position="end"><IconButton aria-label={t('customers.clearSearch')} onClick={handleClearSearch}><ClearIcon /></IconButton></InputAdornment> : undefined,
              }}
            />
            {isMobile && isSuperAdmin && <IconButton aria-label={t('common.filters')} aria-expanded={showFilters} aria-controls="customer-company-filters" onClick={() => setShowFilters(!showFilters)} sx={{ width: 44, height: 44, flexShrink: 0 }}><FilterListIcon /></IconButton>}
            {isMobile && canWrite && <IconButton aria-label={t('customers.addCustomer')} onClick={onAdd} sx={{ width: 44, height: 44, flexShrink: 0, color: 'primary.main' }}><AddIcon sx={{ fontSize: 30 }} /></IconButton>}
            {!isMobile && <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
              {isSuperAdmin && <Button variant="outlined" aria-label={t('common.filters')} aria-expanded={showFilters} aria-controls="customer-company-filters" startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setShowFilters(!showFilters)}>{t('common.filters')}</Button>}
            </Box>}
          </Box>

          {isSuperAdmin && (
            <Collapse in={showFilters} id="customer-company-filters">
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('customers.company')}</InputLabel>
                  <Select value={selectedCompany} label={t('customers.company')} onChange={(e) => handleCompanyChange(e.target.value)}>
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {companies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Collapse>
          )}

          {!isMobile && isSuperAdmin && selectedCompany && <Chip size="small" label={`${t('customers.company')}: ${companies.find(company => company.id === selectedCompany)?.name || selectedCompany}`} onDelete={() => handleCompanyChange('')} sx={{ mt: 1.5 }} />}

        </ListSurface.Toolbar>

        <ListSurface.Content sx={{ position: 'relative' }}>
        <NotebookColumnTab label={t('common.columns')} accent="customers" open={Boolean(columnMenuAnchor)} onClick={handleColumnMenuOpen} />
        {/* Mobile contact list */}
        {!isDesktopTable && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : table.getRowModel().rows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t(searchTerm ? 'customers.noSearchResults' : 'customers.noCustomers')}
                </Typography>
              </Box>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0 }}>
                {table.getRowModel().rows.map((row) => {
                  const customer = row.original;
                  const customerName = `${customer.firstName} ${customer.lastName}`.trim();
                  const initials = getPersonInitials(customer.firstName, customer.lastName, language);
                  const companyDetail = !customer.phone && !customer.email && isSuperAdmin ? customer.companyName || customer.company?.name : undefined;
                  return (
                    <Box component="li"
                      key={customer.id} 
                      sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 68, pl: 1.5, pr: 1, gap: 0.75 }}>
                        <CardActionArea disableRipple onClick={() => onCustomerClick?.(customer.id, customerName)} sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch', ml: -1.5, pl: 2, pr: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.25, borderRadius: 1, textAlign: 'left', '&:active': { bgcolor: 'action.hover' } }}>
                          <IdentityAvatar>
                            {initials || <PersonIcon fontSize="small" />}
                          </IdentityAvatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography component="h3" fontWeight={650} noWrap sx={{ minWidth: 0, fontSize: 16, lineHeight: 1.3 }}>
                              {customerName}
                            </Typography>
                            {(customer.phone || customer.email || companyDetail) && <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                              {customer.phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, minWidth: 0, flexShrink: customer.email ? 1 : 0, fontSize: 12 }}>
                                <PhoneOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.phone}</Box>
                              </Typography>}
                              {customer.email && <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, minWidth: 0, flex: 1, fontSize: 12 }}>
                                <EmailOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.email}</Box>
                              </Typography>}
                              {companyDetail && <Typography variant="caption" color="text.secondary" noWrap>{companyDetail}</Typography>}
                            </Box>}
                          </Box>
                        </CardActionArea>
                        <Box sx={{ '& .MuiIconButton-root': { width: 44, height: 44, color: 'text.secondary' } }}><CustomerActionsMenu customer={customer} onEdit={canWrite ? onEdit : undefined} onDelete={canWrite ? onDelete : undefined} showContactActions /></Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Desktop Table View */}
        {isDesktopTable && (
          <TableContainer>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <DataTableHeaderCell
                      key={header.id}
                      align={header.column.id === 'actions' ? 'right' : 'left'}
                      sort={header.column.getCanSort() ? {
                        active: Boolean(header.column.getIsSorted()),
                        direction: header.column.getIsSorted() === 'desc' ? 'desc' : 'asc',
                        onClick: () => header.column.toggleSorting(),
                      } : undefined}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </DataTableHeaderCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t(searchTerm ? 'customers.noSearchResults' : 'customers.noCustomers')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`customer-row-${row.original.id}`}
                    hover
                    onContextMenu={(e) => handleRowRightClick(e, row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} align={cell.column.id === 'actions' ? 'right' : 'left'}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        </ListSurface.Content>

        {/* Custom Pagination */}
        <ListSurface.Footer>
        {paginatedData && (isMobile || paginatedData.totalElements > 0) && (
          <PaginationFooter
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            totalPages={paginatedData.totalPages}
            totalElements={paginatedData.totalElements}
            showingLabel={t('common.showing')}
            ofLabel={t('common.of')}
            pageSizeLabel={t('common.rowsPerPage')}
            mobileCompact
            previousPageLabel={t('customers.previousPage')}
            nextPageLabel={t('customers.nextPage')}
            onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
            onPageSizeChange={(pageSize) => table.setPageSize(pageSize)}
          />
        )}
        </ListSurface.Footer>
      </ListSurface>

      <Menu
        open={Boolean(contextMenuPosition)}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition !== null
            ? { top: contextMenuPosition.y, left: contextMenuPosition.x }
            : undefined
        }
      >
        <MenuItem onClick={() => { handleContextMenuClose(); contextMenuCustomer && onEdit(contextMenuCustomer); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleContextMenuClose(); contextMenuCustomer && onDelete(contextMenuCustomer); }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <ColumnVisibilityMenu
        side
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        onToggle={(key) => toggleColumnVisibility(key as keyof typeof columnVisibility)}
        items={[
          { key: 'firstName', label: t('customers.name'), visible: columnVisibility.firstName },
          { key: 'phone', label: t('customers.phone'), visible: columnVisibility.phone },
          { key: 'email', label: t('customers.email'), visible: columnVisibility.email },
          { key: 'taxIdentificationNumber', label: t('customers.taxIdentificationNumber'), visible: columnVisibility.taxIdentificationNumber },
          { key: 'address', label: t('customers.address'), visible: columnVisibility.address },
          ...(isSuperAdmin ? [{ key: 'companyName', label: t('customers.company'), visible: columnVisibility.companyName }] : []),
          ...(canWrite ? [{ key: 'actions', label: t('common.actions'), visible: columnVisibility.actions }] : []),
        ]}
      />
    </Box>
  );
});

export default CustomerList;
